import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HrButtons, HrFlexContainer, HrPrimary, HrRed, HrTitleH2 } from '@components/styles/hr-styles';
import { observer } from 'mobx-react-lite';
import { GenericTabs } from '@components/tab';
import { useToast } from '@/hook/useToast';
import { RequestTable } from '@/vacancy_request/components/table';
import { SendRequestModal } from '@/vacancy_request/components/modals/send-request';
import { SendLinkModal } from '@/vacancy_request/components/modals/send-link';
import { ApproveRequestModal } from '@/vacancy_request/components/modals/approve-request';
import { RejectRequestModal } from '@/vacancy_request/components/modals/reject-request';
import { ModifyRequestModal } from '@/vacancy_request/components/modals/modify-modal';
import { DeleteRequestModal } from '@/vacancy_request/components/modals/delete-request';
import { CountersSecctions } from '@/vacancy_request/components/cards';
import {
  approveRequest,
  deleteRequest,
  getActiveVacancyRequestTemplates,
  getAccountVacanciesRequests,
  getDataTable,
  modifyRequest,
  rejectRequest,
  sendLinkVacancyRequest
} from '@/vacancy_request/services/api';
import storeRequest from '@/vacancy_request/store/storeRequest';
import { Records } from '@/vacancy_request/components/records';

const KEY_DATA_CARDS = 'dataCards';
const KEY_DATA_TABLE = 'dataTable';
const KEY_TEMPLATES = 'activeVacancyRequestTemplates';
const PAGE_SIZE = 10;

export const CollaborativeSelection = observer(({ IntercomProductTourId, bannerShow }) => {
  const CREATE_TEMPLATE_PATH = '/app/configuration/vacancy_request';
  const { setActualRowId, actualRowId } = storeRequest();
  const [t] = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [showModalSendRequest, setShowModalSendRequest] = useState(false);
  const [showModalSendLink, setShowModalSendLink] = useState(false);
  const [selectedRequestLink, setSelectedRequestLink] = useState('');
  const [showModalApproveRequest, setShowModalApproveRequest] = useState(false);
  const [showModalModifyRequest, setShowModalModifyRequest] = useState(false);
  const [showModalRejectRequest, setShowModalRejectRequest] = useState(false);
  const [showModalDeleteRequest, setShowModalDeleteRequest] = useState(false);
  const [filter, setFilter] = useState({
    status: null,
    search: '',
    pageSize: PAGE_SIZE,
    pageIndex: 0,
    sortBy: null,
    order: null
  });
  const onShowToast = (toastConfig = {}) => {
    const isError = toastConfig?.type === 'error' || toastConfig?.type === 'warning';
    showToast({
      message: toastConfig?.message || '',
      title: toastConfig?.title || '',
      iconClassName: isError ? 'hr-Alert' : 'hr-Checkmark',
      color: isError ? HrRed : HrPrimary
    });
  };

  const { data: templates = [], isLoading: isLoadingTemplates, refetch: refetchTemplates } = useQuery(
    [KEY_TEMPLATES],
    () => getActiveVacancyRequestTemplates(),
    { initialData: [], staleTime: Infinity }
  );

  const sendLink = useMutation(sendLinkVacancyRequest, {
    onSuccess: () => {
      setShowModalSendLink(false);
      onShowToast({ visible: true, message: t(`requestVacancies.colaborativeSelection.sendMail.success`) });
    }
  });

  const { data: dataCards, isLoading: isLoadingCards, error: errorCards, refetch: refetchCards } = useQuery(
    [KEY_DATA_CARDS],
    () => getAccountVacanciesRequests(),
    {
      staleTime: Infinity
    }
  );

  const { data: dataTable, isLoading: isLoadingTable, error: errorTable, refetch: refetchTable } = useQuery(
    [KEY_DATA_TABLE, filter],
    () => getDataTable(filter),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([KEY_DATA_CARDS]);
      },
      staleTime: Infinity
    }
  );

  const approve = useMutation(approveRequest, {
    onSuccess: (data) => {
      if (data.data.result === 'success') {
        queryClient.invalidateQueries([KEY_DATA_TABLE]);
        setShowModalApproveRequest(false);
        onShowToast({ visible: true, message: t(`requestVacancies.colaborativeSelection.approveRequest`) });
        setFilter((prev) => {
          return { ...prev, pageIndex: 0 };
        });
        window.location.href = data.data.shortUrlVacancy;
      }
      if (data.data.result === 'error')
        onShowToast({ visible: true, type: 'error', message: t(`general.error.support`) });
    }
  });

  const modify = useMutation(modifyRequest, {
    onSuccess: (data) => {
      if (data.data.result === 'success') {
        queryClient.invalidateQueries([KEY_DATA_TABLE]);
        setShowModalModifyRequest(false);
        onShowToast({ visible: true, message: t(`requestVacancies.colaborativeSelection.modifyRequest`) });
        setFilter((prev) => {
          return { ...prev, pageIndex: 0 };
        });
      }

      if (data.data.result === 'error')
        onShowToast({ visible: true, type: 'error', message: t(`general.error.support`) });
    }
  });

  const reject = useMutation(rejectRequest, {
    onSuccess: (data) => {
      if (data.data.result === 'success') {
        queryClient.invalidateQueries([KEY_DATA_TABLE]);
        setShowModalRejectRequest(false);
        onShowToast({ visible: true, message: t(`requestVacancies.colaborativeSelection.rejectRequest`) });
        setFilter((prev) => {
          return { ...prev, pageIndex: 0 };
        });
      }

      if (data.data.result === 'error')
        onShowToast({ visible: true, type: 'error', message: t(`general.error.support`) });
    }
  });

  const delete1 = useMutation(deleteRequest, {
    onSuccess: (data) => {
      if (data.data.result === 'success') {
        queryClient.invalidateQueries([KEY_DATA_TABLE]);
        setShowModalDeleteRequest(false);
        onShowToast({ visible: true, message: t(`requestVacancies.colaborativeSelection.deleteRequest`) });
        setFilter((prev) => {
          return { ...prev, pageIndex: 0 };
        });
      }

      if (data.data.result === 'error')
        onShowToast({ visible: true, type: 'error', message: t(`general.error.support`) });
    },
    onError: (error) => onShowToast({ visible: true, type: 'error', message: t(`general.error.support`) })
  });

  useEffect(() => {
    refetchTemplates();
    refetchCards();
    refetchTable();
  }, []);

  useEffect(() => {
    refetchTable();
  }, [filter]);

  const tabs = [
    {
      label: 'Solicitudes',
      content: (
        <RequestTable
          response={dataTable}
          dataTable={dataTable?.data ?? []}
          isLoading={isLoadingTable}
          isError={errorTable}
          setFilter={setFilter}
          setShowModalApproveRequest={setShowModalApproveRequest}
          setShowModalRejectRequest={setShowModalRejectRequest}
          setShowModalModifyRequest={setShowModalModifyRequest}
          setShowModalDeleteRequest={setShowModalDeleteRequest}
          setActualRowId={setActualRowId}
          onShowToast={onShowToast}
        />
      )
    },
    { label: 'Historial', content: <Records /> }
  ];

  const handleOpenSendLinkModal = (requestLink) => {
    setSelectedRequestLink(requestLink);
    setShowModalSendRequest(false);
    setShowModalSendLink(true);
  };

  const handleGoToCreateTemplate = (event) => {
    event.preventDefault();
    window.location.assign(CREATE_TEMPLATE_PATH);
  };

  return (
    <HrFlexContainer flexDirection='column' gap='16px'>
      {showModalSendRequest && (
        <SendRequestModal
          templates={templates}
          setShowModalSendRequest={setShowModalSendRequest}
          onShowToast={onShowToast}
          onOpenSendLink={handleOpenSendLinkModal}
          onRefreshTemplates={refetchTemplates}
        />
      )}
      {showModalSendLink && (
        <SendLinkModal
          sendLink={sendLink}
          setShowModalSendLink={setShowModalSendLink}
          onShowToast={onShowToast}
          requestLink={selectedRequestLink}
        />
      )}
      {showModalApproveRequest && (
        <ApproveRequestModal
          actualRowId={actualRowId}
          approve={approve}
          setShowModalApproveRequest={setShowModalApproveRequest}
        />
      )}
      {showModalModifyRequest && (
        <ModifyRequestModal
          actualRowId={actualRowId}
          modify={modify}
          setShowModalModifyRequest={setShowModalModifyRequest}
          onShowToast={onShowToast}
        />
      )}
      {showModalRejectRequest && (
        <RejectRequestModal
          actualRowId={actualRowId}
          reject={reject}
          setShowModalRejectRequest={setShowModalRejectRequest}
          onShowToast={onShowToast}
        />
      )}
      {showModalDeleteRequest && (
        <DeleteRequestModal
          actualRowId={actualRowId}
          delete1={delete1}
          setShowModalDeleteRequest={setShowModalDeleteRequest}
          onShowToast={onShowToast}
        />
      )}
      <HrFlexContainer justifyContent='space-between'>
        <div style={{ padding: 0, margin: 0, display: 'flex', alignItems: 'center' }}>
          <HrTitleH2 margin='0 !important' fontWeight='600 !important' color='#3B3F51 !important'>
            {t(`requestVacancies.colaborativeSelection.title`)}
          </HrTitleH2>
          {IntercomProductTourId && (
            <button
              type='button'
              id='toggleBannerButton'
              onClick={() => window.toggleBanner('vacancyRequestBanner')}
              className='hrc-tag-button'
            >
              <div />
              <span>¿Qué puedo hacer aquí? 🤔</span>
            </button>
          )}
        </div>
        <HrFlexContainer flexWrap='nowrap' width='auto' gap='8px'>
          <HrButtons
            category='tertiaryGray'
            size='medium'
            icon
            onClick={handleGoToCreateTemplate}
            disabled={isLoadingTemplates || templates.length === 0}
          >
            <i className='hr-File' />
            Ver plantillas
          </HrButtons>
          <HrButtons category='tertiaryGreen' size='medium' icon onClick={() => setShowModalSendRequest(true)}>
            <i className='hr-Link' style={{ color: HrPrimary }} />
            {t(`requestVacancies.colaborativeSelection.sendRequest`)}
          </HrButtons>
        </HrFlexContainer>
      </HrFlexContainer>

      <div
        id='vacancyRequestBanner'
        style={{
          width: '100%',
          borderRadius: '8px',
          background: '#F4F4F4',
          display: bannerShow ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          padding: '8px',
          border: '1px solid #dadada',
          position: 'relative'
        }}
      >
        <button
          type='button'
          onClick={() => window.toggleBanner('vacancyRequestBanner')}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            cursor: 'pointer',
            fontSize: '24px',
            backgroundColor: 'transparent',
            border: 'none'
          }}
          className='hr-Cross'
          aria-label='Close Banner'
        />
        <div style={{ paddingLeft: '32px', maxWidth: '1000px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <h3 style={{ fontWeight: '600', fontSize: '14px', color: '#3B3F51' }}>
            <strong>¿Deseas iniciar el proceso de solicitud de vacante desde Hiring Room?</strong>
          </h3>
          <p style={{ fontWeight: '400', fontSize: '14px', color: '#3B3F51' }}>
            En esta sección podrás compartir un link público a tus líderes para que soliciten la apertura de una vacante
            desde el botón &quot;Enviar solicitud&quot;. Luego podrás gestionarlas con las acciones
            <strong>
              &quot;rechazar&quot;, &quot;solicitar modificaciones&quot;, &quot;eliminar&quot; o &quot;aprobar&quot;.
            </strong>{' '}
            Ésta última hará una redirección a la creación de vacante con los datos que hayan completado anteriormente
            en la solicitud.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '16px' }}>
            <button
              type='button'
              style={{
                fontWeight: 600,
                fontSize: '14px',
                color: '#24BB72',
                textDecoration: 'none',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
              onClick={() => window.showProductTour(IntercomProductTourId)}
            >
              <span style={{ color: '#24BB72', marginRight: '4px' }} className='hr-Tour' />
              Iniciar recorrido guiado
            </button>
            <a
              style={{ fontWeight: '600', fontSize: '14px', color: '#24BB72', textDecoration: 'none' }}
              href='https://intercom.help/hiringroom/es/articles/6884301-solicitud-de-vacante'
              target='_blank'
              rel='noreferrer'
            >
              <span style={{ color: '#24BB72', marginRight: '4px' }} className='hr-Question' />
              Ver más información
            </a>
          </div>
        </div>
        <figure style={{ margin: '0' }}>
          <img src='/assets/img/app/banners/vacancyRequestBanner.svg' alt='a' />
        </figure>
      </div>

      <CountersSecctions data={dataCards} isLoading={isLoadingCards} />
      <GenericTabs tabs={tabs} />
    </HrFlexContainer>
  );
});
